/**
 * CommandPalette — global Cmd+K overlay.
 *
 * Opens on:
 *   - Keyboard: Cmd+K (Mac) / Ctrl+K (Windows)
 *   - Click:    The search bar in SiteHeader dispatches 'open-command-palette'
 *
 * Features:
 *   - Fuzzy text filter across navigate + quick-action items
 *   - Free-form AI query → routes to /assistant with the query prefilled
 *   - Keyboard navigation: ↑ ↓ Enter to select, Esc to close
 *   - Permission-aware: locked items shown but unclickable
 *   - Section headers: NAVIGATE / QUICK ACTIONS / (when typing) ASK AI
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../hooks/usePermissions";
import { ENABLED_NAV_PATHS } from "../constants/navGate";

/** Routes not yet migrated to the AI-native redesign are shown locked, mirroring AppSidebar. */
const isPathEnabled = (path?: string): boolean =>
  !path || ENABLED_NAV_PATHS.has(path.split("?")[0]);

// ── Types ──────────────────────────────────────────────────────────────────

interface PaletteItem {
  id: string;
  label: string;
  path?: string;
  description?: string;
  icon: string;
  section: "navigate" | "action" | "ai";
  action: () => void;
  permissionKey?: string;
  shortcut?: string;
}

// ── Static item definitions ────────────────────────────────────────────────

const NAV_ITEMS = [
  { id: "home",        label: "Home",             description: "Intelligence feed & recent activity", icon: "🏠", path: "/",               permissionKey: "home",               shortcut: "⌘1" },
  { id: "assistant",   label: "Ask Adrsta",       description: "Chat with agents about your data",     icon: "💬", path: "/workspace",      permissionKey: "ai_assistant",        shortcut: "⌘2" },
  { id: "campaigns",   label: "Campaigns",        description: "Performance agent & campaign plans",   icon: "📊", path: "/campaigns",      permissionKey: "campaigns",           shortcut: "⌘3" },
  { id: "cc-ml",       label: "Command Center + ML", description: "Geo tests with Adrsta power analysis & readout", icon: "▦", path: "/command-center/ml", permissionKey: "experiments", shortcut: "" },
  { id: "cc-deck",     label: "Command Center (no ML)", description: "Geo tests measured by a partner deck",      icon: "▦", path: "/command-center/deck", permissionKey: "experiments", shortcut: "" },
  { id: "experiments", label: "Experiments",      description: "Synthetic control & geo lift studies", icon: "🧪", path: "/experiments",    permissionKey: "experiments",         shortcut: "⌘4" },
  { id: "power-calc",  label: "Power Calculator", description: "Design experiments & power analysis",  icon: "⚡", path: "/power-calculator", permissionKey: "power_calculator",   shortcut: "" },
  { id: "mmm",         label: "MMM",              description: "Marketing mix model & channel ROI",    icon: "📈", path: "/mmm",            permissionKey: "mmm",                 shortcut: "⌘5" },
  { id: "bid",         label: "Bid Optimizer",    description: "Audience segments & bid strategies",   icon: "🎯", path: "/bid-optimizer",  permissionKey: "bid_optimizer",       shortcut: "" },
  { id: "history",     label: "Past Work",        description: "All jobs, experiments & conversations",icon: "🕐", path: "/history",        permissionKey: "history",             shortcut: "" },
  { id: "reports",     label: "Reports",          description: "AI-generated narrative reports",        icon: "📁", path: "/reports",        permissionKey: "reports",             shortcut: "" },
  { id: "settings",    label: "Settings",         description: "Integrations, team & API keys",        icon: "⚙️", path: "/settings",       permissionKey: "home",                shortcut: "" },
];

const ACTION_ITEMS = [
  { id: "new-experiment",  label: "New Experiment",       description: "Start a new synthetic control study",  icon: "＋", path: "/experiments?mode=create",   permissionKey: "experiments" },
  { id: "new-power-calc",  label: "Run Power Calculator", description: "Design a new test & calculate power",  icon: "⚡", path: "/power-calculator",           permissionKey: "power_calculator" },
  { id: "new-campaign",    label: "Start New Campaign",   description: "Launch the Performance Agent flow",    icon: "🚀", path: "/campaigns?mode=create",     permissionKey: "campaigns" },
  { id: "run-mmm",         label: "Run MMM Model",        description: "Start a new marketing mix model run",  icon: "⟳", path: "/mmm?action=run",             permissionKey: "mmm" },
];

// ── Component ──────────────────────────────────────────────────────────────

const CommandPalette: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { checkMenuPermission } = usePermissions();

  // ── Open/close listeners ─────────────────────────────────────────────────

  useEffect(() => {
    const openHandler = () => { setOpen(true); setQuery(""); setSelectedIdx(0); };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("open-command-palette", openHandler);
    window.addEventListener("keydown", keyHandler);
    return () => {
      window.removeEventListener("open-command-palette", openHandler);
      window.removeEventListener("keydown", keyHandler);
    };
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  // ── Build filtered items ─────────────────────────────────────────────────

  const allItems: PaletteItem[] = [
    ...NAV_ITEMS.map(n => ({
      id: n.id,
      label: n.label,
      description: n.description,
      icon: n.icon,
      section: "navigate" as const,
      shortcut: n.shortcut,
      permissionKey: n.permissionKey,
      path: n.path,
      action: () => { navigate(n.path); setOpen(false); },
    })),
    ...ACTION_ITEMS.map(a => ({
      id: a.id,
      label: a.label,
      description: a.description,
      icon: a.icon,
      section: "action" as const,
      permissionKey: a.permissionKey,
      path: a.path,
      action: () => { navigate(a.path); setOpen(false); },
    })),
  ];

  const q = query.trim().toLowerCase();

  const filtered = q
    ? allItems.filter(i =>
        i.label.toLowerCase().includes(q) ||
        (i.description || "").toLowerCase().includes(q)
      )
    : allItems;

  // Show "Ask AI" option when there's a non-nav query
  const showAskAI = q.length > 2 && !filtered.some(i => i.label.toLowerCase() === q);
  const askAIItem: PaletteItem = {
    id: "ask-ai",
    label: `Ask AI: "${query}"`,
    description: "Send this question to the Study Assistant",
    icon: "✦",
    section: "ai",
    action: () => {
      navigate(`/assistant?q=${encodeURIComponent(query)}`);
      setOpen(false);
    },
  };

  const displayItems: PaletteItem[] = showAskAI ? [askAIItem, ...filtered] : filtered;

  // ── Keyboard navigation ───────────────────────────────────────────────────

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, displayItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = displayItems[selectedIdx];
      if (item) {
        const hasPerm = (!item.permissionKey || checkMenuPermission("/" + item.id)) && isPathEnabled(item.path);
        if (hasPerm || item.section === "ai") item.action();
      }
    }
  };

  // Reset selection when query changes
  useEffect(() => setSelectedIdx(0), [query]);

  if (!open) return null;

  // ── Section grouping ──────────────────────────────────────────────────────

  const sections: { key: string; label: string; items: PaletteItem[] }[] = [];

  if (showAskAI) {
    sections.push({ key: "ai", label: "ASK AI", items: [askAIItem] });
  }

  const navItems = filtered.filter(i => i.section === "navigate");
  const actionItems = filtered.filter(i => i.section === "action");

  if (!q) {
    if (navItems.length) sections.push({ key: "navigate", label: "NAVIGATE", items: navItems });
    if (actionItems.length) sections.push({ key: "action", label: "QUICK ACTIONS", items: actionItems });
  } else {
    const all = [...navItems, ...actionItems];
    if (all.length) sections.push({ key: "results", label: "RESULTS", items: all });
  }

  let globalIdx = showAskAI ? 1 : 0;

  return (
    <>
      <style>{`
        @keyframes palette-backdrop { from{opacity:0} to{opacity:1} }
        @keyframes palette-modal   { from{opacity:0;transform:translateY(-12px) scale(0.98)} to{opacity:1;transform:none} }
        .palette-backdrop { animation: palette-backdrop 0.15s ease; }
        .palette-modal    { animation: palette-modal    0.15s ease; }
        .palette-item { display:flex; align-items:center; gap:10px; padding:8px 12px; border-radius:6px; cursor:pointer; transition:background 80ms ease; }
        .palette-item:hover, .palette-item.selected { background:var(--bg-hover); }
        .palette-item.selected { background:var(--blue-subtle) !important; }
        .palette-item.locked { opacity:0.4; cursor:not-allowed; }
        .palette-item.ai-item { background:var(--violet-subtle) !important; }
        .palette-item.ai-item.selected { background:rgba(139,92,246,0.25) !important; }
      `}</style>

      {/* Backdrop */}
      <div
        className="palette-backdrop fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
        style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
        onClick={() => setOpen(false)}
      >
        {/* Modal */}
        <div
          className="palette-modal w-full"
          style={{
            maxWidth: 580,
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
            overflow: 'hidden',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Input */}
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '12px 16px',
              borderBottom: '1px solid var(--border-subtle)',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
            </svg>
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a command or ask a question..."
              style={{
                flex: 1, background: 'transparent', border: 'none', outline: 'none',
                color: 'var(--text-primary)', fontSize: 15,
                fontFamily: 'var(--font-sans)',
              }}
            />
            {query && (
              <button onClick={() => setQuery("")} style={{ color: 'var(--text-tertiary)', fontSize: 11, padding: '2px 6px', background: 'var(--bg-hover)', borderRadius: 4, border: '1px solid var(--border-subtle)' }}>
                Clear
              </button>
            )}
          </div>

          {/* Results */}
          <div style={{ maxHeight: 380, overflowY: 'auto', padding: '6px 8px 8px' }}>
            {sections.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--text-tertiary)', fontSize: 13 }}>
                No results for "{query}"
                <div style={{ marginTop: 8, fontSize: 12 }}>Try a page name or action</div>
              </div>
            ) : (
              sections.map(section => {
                const startIdx = globalIdx;
                globalIdx += section.items.length;
                return (
                  <div key={section.key}>
                    <div style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                      color: 'var(--text-tertiary)', padding: '8px 10px 4px',
                      fontFamily: 'var(--font-sans)',
                    }}>
                      {section.label}
                    </div>
                    {section.items.map((item, itemIdx) => {
                      const absoluteIdx = startIdx + itemIdx;
                      const hasPerm = (!item.permissionKey || checkMenuPermission("/" + item.id)) && isPathEnabled(item.path);
                      const isSelected = absoluteIdx === selectedIdx;

                      return (
                        <div
                          key={item.id}
                          className={`palette-item${isSelected ? ' selected' : ''}${!hasPerm ? ' locked' : ''}${item.section === 'ai' ? ' ai-item' : ''}`}
                          onClick={() => { if (hasPerm || item.section === "ai") item.action(); }}
                        >
                          <span style={{ fontSize: 16, width: 22, textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13.5, color: 'var(--text-primary)', fontFamily: 'var(--font-sans)', fontWeight: item.section === 'ai' ? 500 : 400 }}>
                              {item.label}
                            </div>
                            {item.description && (
                              <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 1, fontFamily: 'var(--font-sans)' }}>
                                {item.description}
                              </div>
                            )}
                          </div>
                          {(item as any).shortcut && (
                            <kbd style={{ fontSize: 10, color: 'var(--text-tertiary)', background: 'var(--bg-hover)', border: '1px solid var(--border-subtle)', borderRadius: 4, padding: '2px 5px', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
                              {(item as any).shortcut}
                            </kbd>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div style={{
            borderTop: '1px solid var(--border-subtle)',
            padding: '8px 14px',
            display: 'flex', gap: 16, alignItems: 'center',
          }}>
            {[['↑↓', 'navigate'], ['↵', 'select'], ['esc', 'close']].map(([key, label]) => (
              <div key={key} style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                <kbd style={{ fontSize: 10, color: 'var(--text-tertiary)', background: 'var(--bg-hover)', border: '1px solid var(--border-subtle)', borderRadius: 4, padding: '2px 5px', fontFamily: 'var(--font-mono)' }}>{key}</kbd>
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontFamily: 'var(--font-sans)' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default CommandPalette;
